/**
 * Who is signed in, for the whole app.
 *
 * The session is three httpOnly cookies the fleet set at `/api/callback`; nothing here can read
 * them. So "am I signed in" is answered by asking the server - `get_user` - and the only state
 * worth holding is the answer.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { apiFetch, apiPost, API_PREFIX, HTTP_FORBIDDEN, HTTP_TOO_MANY_REQUESTS } from "@/api/apiClient";

export interface User {
  id: string;
  email: string;
  name: string;
  /** The name in parts, where the identity provider supplied them. */
  given_name?: string;
  family_name?: string;
  groups: string[];
  roles: string[];
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /**
   * Signed in, and allowed nothing.
   *
   * Roles come from Cognito group membership alone, so an account in no group - or in a group this
   * fleet does not map - authenticates perfectly and is then refused everywhere, `get_user`
   * included. Without this the app cannot tell that apart from having no session, shows the login
   * page, and signs the same account straight back in: a loop with no error anywhere on screen.
   */
  accessDenied: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async (): Promise<void> => {
    try {
      const response = await apiPost("get_user");

      if (response.ok) {
        const payload = (await response.json()) as { data: User };
        setUser(payload.data);
        setAccessDenied(false);
        return;
      }

      if (response.status === HTTP_FORBIDDEN) {
        // The session is real; this account simply holds no role that reaches anything.
        setAccessDenied(true);
        setUser(null);
        return;
      }

      if (response.status === HTTP_TOO_MANY_REQUESTS) {
        // Being throttled says nothing about whether the session is still valid, so the user keeps
        // whatever we already knew about them rather than being bounced to the login page.
        console.warn("Rate limited while checking the session; keeping the current user");
        return;
      }

      // Anything else - 401 after a failed refresh included - means no usable session.
      //
      // Unlike bioapp v1 there is no "404 means this server has no auth" branch. That exists for a
      // biopod running open; a fleet either loads an authentication provider or refuses to start,
      // so a 404 here is a broken deployment, not an invitation to continue anonymously.
      setUser(null);
    } catch (error) {
      console.error("Failed to check the session:", error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchUser();
  }, [fetchUser]);

  const login = useCallback(async (): Promise<void> => {
    // The whole path, not just the origin, so a deep link survives the Cognito round trip.
    const returnUrl = encodeURIComponent(window.location.origin + window.location.pathname);
    const response = await apiFetch(`${API_PREFIX}/login?return_url=${returnUrl}`, { method: "POST" });
    const payload = (await response.json()) as { data?: { auth_url?: string } };

    if (!payload.data?.auth_url) {
      throw new Error("The server did not return a sign-in URL.");
    }
    window.location.href = payload.data.auth_url;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    // Back to the root rather than the current page: the session is gone, so anything else would
    // only bounce to the login screen anyway.
    const returnUrl = encodeURIComponent(window.location.origin + "/");
    try {
      const response = await apiFetch(`${API_PREFIX}/logout?return_url=${returnUrl}`, { method: "POST" });
      if (response.ok) {
        const payload = (await response.json()) as { data?: { logout_url?: string } };
        setUser(null);
        if (payload.data?.logout_url) {
          window.location.href = payload.data.logout_url;
          return;
        }
      }
    } catch (error) {
      console.error("Sign out failed:", error);
    }
    // Whatever the server said, stop acting as this user locally.
    setUser(null);
    setAccessDenied(false);
  }, []);

  const value: AuthState = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    accessDenied,
    login,
    logout,
    refreshUser: fetchUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
